import { appQueryClient } from "app/AppQueryClient";
import { capitalActivityRoutes } from "features/capital-activity/CapitalActivityRoutes";
import { closingRoutes } from "features/closings/ClosingRoutes";
import { createCommitmentRoutes } from "features/commitments/CommitmentRoutes";
import { creditFacilityRoutes } from "features/credit-facility/CreditFacilityRoutes";
import { feeRoutes } from "features/fees/FeeRoutes";
import { financialReportingRoutes } from "features/financial-reporting/FinancialReportingRoutes";
import FundFamilyLayout from "features/fund-family/FundFamilyLayouts";
import { FundFamilyNotFoundPage } from "features/fund-family/FundFamilyNotFoundPage";
import { fundFamilySetupRoutes } from "features/fund-family/setup/FundFamilySetupRoutes";
import {
  investmentRoutes,
  portfolioRoutes,
} from "features/investments/InvestmentRoutes";
import { investorTransferRoutes } from "features/investor-transfer/InvestorTransferRoutes";
import { getCreditFacilityListLoader } from "gen/endpoints/credit-facility/credit-facility";
import {
  fundFamilyRetrieve,
  getFundFamilyRetrieveLoader,
  getFundFamilyRetrieveQueryKey,
} from "gen/endpoints/fund-family/fund-family";
import { getFundFamilyTableDataRetrieveLoader } from "gen/endpoints/fund-family-table-data/fund-family-table-data";
import { FundFamilyStateMachineFieldsEnum } from "gen/model/fundFamilyStateMachineFieldsEnum";
import { redirect, RouteObject } from "react-router-dom";
import { isFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { composeLoaders } from "shared/utils/RoutingUtils";

const fundFamilyRoutes: RouteObject = {
  id: "fundFamilies",
  path: "fund-family",
  children: [
    {
      id: "fundFamilyList",
      index: true,
      lazy: async () => {
        const { FundFamilyListPage } = await import(
          "features/fund-family/FundFamilyListPage"
        );
        return { Component: FundFamilyListPage };
      },
      loader: getFundFamilyTableDataRetrieveLoader(),
    },
    {
      id: "fundFamily",
      path: ":fundFamilyId",
      loader: getFundFamilyRetrieveLoader(({ fundFamilyId }) => ({
        fundFamilyId: fundFamilyId!,
      })),
      children: [
        {
          id: "fundFamilyIndex",
          index: true,
          loader: async ({ params: { fundFamilyId } }) => {
            const fundFamily = await appQueryClient.ensureQueryData({
              queryKey: getFundFamilyRetrieveQueryKey(fundFamilyId!),
              queryFn: () => fundFamilyRetrieve(fundFamilyId!),
            });
            const isFundFamilySetupActive =
              await isFeatureFlagActive("fund_family_setup");
            const isDraft =
              fundFamily.state === FundFamilyStateMachineFieldsEnum.draft;
            if (isDraft && isFundFamilySetupActive) {
              return redirect("setup/fund");
            } else {
              return redirect("commitments");
            }
          },
        },
        {
          id: "fundFamilyLayout",
          element: <FundFamilyLayout />,
          children: [
            capitalActivityRoutes,
            createCommitmentRoutes("fundFamily"),
            investorTransferRoutes,
            closingRoutes,
            creditFacilityRoutes,
            feeRoutes,
            fundFamilySetupRoutes,
            investmentRoutes,
            portfolioRoutes,
            financialReportingRoutes,
            {
              id: "fundFamilyNotFound",
              path: "*",
              handle: {
                showSidebar: true,
              },
              element: <FundFamilyNotFoundPage />,
            },
          ],
          loader: composeLoaders(getCreditFacilityListLoader()),
        },
      ],
    },
  ],
};

export default fundFamilyRoutes;
